/**
 * @fileoverview Listing Helper Functions
 *
 * Ilan verilerini islemek icin yardimci fonksiyonlar.
 * useListingDetail hook'undan extract edildi.
 *
 * @module utils/listing-helpers
 */

/**
 * quickInfos dizisinden belirli bir anahtarin degerini cikarir.
 *
 * @param {Array<{key: string, value: string}>} quickInfos - Hizli bilgi dizisi
 * @param {string} key - Aranacak anahtar (ornegin: "building-age", "heating")
 * @returns {string|null} Bulunan deger veya null
 *
 * @example
 * const heating = getQuickInfo(quickInfos, "heating");
 * // Returns: "Dogalgaz" or null
 */
export function getQuickInfo(quickInfos, key) {
  return quickInfos?.find((q) => q.key === key)?.value ?? null;
}

/**
 * Bina yasi string'ini sayiya donusturur.
 *
 * Emlakjet API'si bina yasini string olarak dondurur (ornegin "5 yasinda",
 * "10-15 yasinda"). Bu fonksiyon bu string'i parse ederek sayi dondurur.
 * Aralik verilmisse (10-15 gibi) buyuk degeri dondurur.
 *
 * @param {string|null} ageStr - Bina yasi string'i
 * @returns {number|null} Parse edilmis bina yasi veya null
 *
 * @example
 * parseBuildingAge("5 yasinda")      // Returns: 5
 * parseBuildingAge("10-15 yasinda")  // Returns: 15
 * parseBuildingAge("21-25 yasinda")  // Returns: 25
 * parseBuildingAge(null)             // Returns: null
 */
export function parseBuildingAge(ageStr) {
  if (!ageStr) return null;
  // Match patterns like "5", "10-15", "21-25"
  const match = ageStr.match(/(\d+)(?:-(\d+))?/);
  if (!match) return null;
  // Return the higher number if range, otherwise the single number
  return parseInt(match[2] || match[1], 10);
}

/**
 * Ilanin belirli gun icinde olusturulup olusturulmadigini kontrol eder.
 *
 * Yeni ilanlari tespit etmek icin kullanilir. Varsayilan olarak
 * son 3 gun icinde olusturulan ilanlar "yeni" olarak kabul edilir.
 *
 * @param {string|null} createdAt - ISO 8601 formatinda olusturulma tarihi
 * @param {number} [days=3] - Kac gun icinde "yeni" sayilacagi
 * @returns {boolean} Ilan yeni ise true, degilse false
 *
 * @example
 * isNewListing("2026-01-15T10:00:00Z", 3)  // true (bugunden 2 gun once)
 * isNewListing("2026-01-10T10:00:00Z", 3)  // false (7 gun once)
 */
export function isNewListing(createdAt, days = 3) {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now - created) / (1000 * 60 * 60 * 24);
  return diffDays <= days;
}

/**
 * Attributes dizisinden belirli kategorideki ozellikleri cikarir.
 *
 * @param {Array} attributes - Ozellik gruplari dizisi
 * @param {string} categoryName - Aranacak kategori adi (kismi eslesme)
 * @returns {Array<string>} Kategori icindeki ozellik listesi
 *
 * @example
 * getAttributesByCategory(attributes, "site") // ["Havuz", "Guvenlik", "Otopark"]
 */
export function getAttributesByCategory(attributes, categoryName) {
  if (!Array.isArray(attributes)) return [];
  const found = attributes.find((a) =>
    a.category?.toLowerCase().includes(categoryName.toLowerCase())
  );
  return found?.items || [];
}

/**
 * Properties dizisinden belirli key'in degerini cikarir.
 *
 * @param {Array} properties - Ozellik dizisi ({key, name, value, unit})
 * @param {string} key - Aranacak ozellik anahtari
 * @returns {string|null} Ozellik degeri veya null
 */
export function getPropertyValue(properties, key) {
  if (!Array.isArray(properties)) return null;
  const found = properties.find((p) => p.key === key);
  return found?.value || null;
}
